// src/pages/msp-inventory/pages/reports/SupplierReportPage.jsx
import React, { useEffect, useState } from "react";
import DateRangePicker, { rangeToParams } from "../../components/reports/DateRangePicker";
import KpiCard from "../../components/reports/KpiCard";
import { SortableTH, Pagination } from "../../components/reports/TableControls";
import { getSupplierReport } from "../../services/catalogService";
import { usePersistedState } from "../../hooks/usePersistedState";
import { useSortAndPaginate } from "../../hooks/useSortAndPaginate";
import RefreshableSection from "../../components/reports/RefreshableSection";
import { buildCacheKey, getCachedReport, setCachedReport, invalidateCachedReport } from "../../utils/reportCache";
import RefreshReportBar from "../../components/reports/RefreshReportBar";
import ExportCsvButton from "../../components/reports/ExportCsvButton";
import { downloadCsv } from "../../utils/exportCsv";
import LoadingGame from "../../components/reports/LoadingGame";
import LineItemsDrawer, { ClickableTotal } from "../../components/reports/LineItemsDrawer";
import { AlertTriangle, List } from "lucide-react";

// Fee breakdown hover for a supplier row — uses fixed positioning via
// getBoundingClientRect (same technique as ProfitFeeHover/FeeBreakdownHover
// elsewhere in the app) instead of CSS absolute positioning, since the
// table's own overflow-x-auto wrapper forces overflow-y to auto too and
// clips any absolutely-positioned popup near the top/bottom edge.
const SupplierFeeHover = ({ supplier }) => {
    const [pos, setPos] = useState(null);
    const anchorRef = React.useRef(null);
    const popoverWidth = 224;

    const handleEnter = () => {
        const rect = anchorRef.current?.getBoundingClientRect();
        if (!rect) return;
        const left = Math.min(Math.max(rect.left, 8), window.innerWidth - popoverWidth - 8);
        const top = Math.max(rect.top - 8, 8);
        setPos({ top, left });
    };

    const b = supplier.fee_breakdown_totals;

    return (
        <span
            ref={anchorRef}
            className="text-red-600 cursor-help"
            onMouseEnter={handleEnter}
            onMouseLeave={() => setPos(null)}
        >
            ${supplier.platform_fees.toFixed(2)}
            {pos && (
                <div
                    className="fixed z-50 bg-white border border-gray-200 rounded-xl shadow-2xl p-3 text-left normal-case pointer-events-none"
                    style={{ top: pos.top, left: pos.left, width: popoverWidth, transform: "translateY(-100%)" }}
                >
                    <p className="text-[10px] font-bold text-gray-500 uppercase mb-1.5">Fee Breakdown</p>
                    <div className="space-y-1 text-xs">
                        <div className="flex justify-between"><span className="text-gray-500">Referral</span><span className="font-semibold text-gray-700">${b.referral_fee.toFixed(2)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">WFS Fee</span><span className="font-semibold text-gray-700">${b.wfs_fee.toFixed(2)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Fulfillment</span><span className="font-semibold text-gray-700">${b.fulfillment_cost.toFixed(2)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Inbound</span><span className="font-semibold text-gray-700">${b.inbound_shipping.toFixed(2)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Sales Tax</span><span className="font-semibold text-gray-700">${b.sales_tax.toFixed(2)}</span></div>
                        <div className="flex justify-between pt-1 border-t border-gray-100"><span className="text-gray-600 font-bold">Total</span><span className="font-bold text-red-600">${supplier.platform_fees.toFixed(2)}</span></div>
                    </div>
                </div>
            )}
        </span>
    );
};

const SupplierReportPage = ({ onOpenSku }) => {
    const [range, setRange] = usePersistedState("reports:supplier:range", { key: "90d" });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [breakdown, setBreakdown] = useState(null);
    const openBreakdown = (opts) => setBreakdown({ defaultView: "lines", defaultMissingOnly: false, ...opts });
    const rangeLabel = range.key === "custom" ? `${range.start} → ${range.end}` : `Range: ${range.key.toUpperCase()}`;

    const cacheKey = buildCacheKey("supplier", rangeToParams(range));

    const loadReport = async (forceRefresh = false) => {
        const isInitialLoad = !data;

        if (!forceRefresh) {
            const cached = await getCachedReport(cacheKey);
            // Guard against a stale cached response from before totals/
            // fee_breakdown_totals existed on this report — treat it as a
            // cache miss rather than crashing on data.totals being undefined.
            if (cached && cached.data && cached.data.totals) {
                setData(cached.data);
                setSavedAt(cached.savedAt);
                setLoading(false);
                return;
            }
        }

        if (isInitialLoad) setLoading(true);
        else setRefreshing(true);
        try {
            const d = await getSupplierReport(rangeToParams(range));
            setData(d);
            setSavedAt(Date.now());
            setCachedReport(cacheKey, d);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        loadReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range]);

    const handleForceRefresh = async () => {
        await invalidateCachedReport(cacheKey);
        await loadReport(true);
    };

    const { sorted, pageItems, page, setPage, totalPages, sortConfig, requestSort, pageSize } =
        useSortAndPaginate(data?.rows || [], { key: "revenue", dir: "desc" }, "reports:supplier:table");

    if (loading && !data) return <LoadingGame label="Loading supplier report..." />;
    if (!data) return null;

    const { totals } = data;

    // Click a supplier → its best-selling listings (switchable to order lines).
    const openSupplier = (s, missingOnly = false) => openBreakdown({
        title: s.supplier,
        subtitle: `${rangeLabel} · best-selling listings`,
        params: { ...rangeToParams(range), supplier: s.supplier },
        defaultView: "skus",
        defaultMissingOnly: missingOnly,
        reconcile: { label: "Revenue", field: "revenue", expected: s.revenue },
    });

    const drillRange = (missingOnly = false) => openBreakdown({
        title: missingOnly ? "Order lines missing cost" : "All order lines",
        subtitle: rangeLabel,
        params: rangeToParams(range),
        defaultView: missingOnly ? "lines" : "skus",
        defaultMissingOnly: missingOnly,
        reconcile: missingOnly ? undefined : { label: "Revenue", field: "revenue", expected: totals.revenue },
    });

    return (
        <div className="max-w-6xl mx-auto py-8 px-6">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Supplier Report</h1>
                    <p className="text-gray-500 text-sm">Sales performance broken down by supplier</p>
                </div>
                <div className="flex items-center gap-2">
                    <RefreshReportBar savedAt={savedAt} refreshing={refreshing} onRefresh={handleForceRefresh} onAutoRefresh={handleForceRefresh} />
                    <button type="button" onClick={() => drillRange(false)}
                        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
                        <List size={14} /> All Listings
                    </button>
                    <button type="button" onClick={() => drillRange(true)}
                        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100">
                        <AlertTriangle size={14} /> Missing Costs{(totals.missing_cost_count || 0) > 0 ? ` (${totals.missing_cost_count})` : ""}
                    </button>
                    <ExportCsvButton
                        label="Download Fees Report"
                        onExport={() => downloadCsv(
                            `supplier_fees_report_${new Date().toISOString().slice(0, 10)}.csv`,
                            ["Supplier", "Referral Fee", "WFS Fee", "Fulfillment Cost", "Inbound Shipping", "Sales Tax", "Total Platform Fees"],
                            sorted.map((s) => [
                                s.supplier,
                                s.fee_breakdown_totals.referral_fee.toFixed(2),
                                s.fee_breakdown_totals.wfs_fee.toFixed(2),
                                s.fee_breakdown_totals.fulfillment_cost.toFixed(2),
                                s.fee_breakdown_totals.inbound_shipping.toFixed(2),
                                s.fee_breakdown_totals.sales_tax.toFixed(2),
                                s.platform_fees.toFixed(2),
                            ]),
                            [
                                ["Referral Fee", totals.fee_breakdown_totals.referral_fee.toFixed(2)],
                                ["WFS Fee", totals.fee_breakdown_totals.wfs_fee.toFixed(2)],
                                ["Fulfillment Cost", totals.fee_breakdown_totals.fulfillment_cost.toFixed(2)],
                                ["Inbound Shipping", totals.fee_breakdown_totals.inbound_shipping.toFixed(2)],
                                ["Sales Tax", totals.fee_breakdown_totals.sales_tax.toFixed(2)],
                                ["Total Platform Fees", totals.platform_fees.toFixed(2)],
                            ]
                        )}
                    />
                    <ExportCsvButton label="FULL Report" onExport={() => downloadCsv(
                        `supplier_report_${new Date().toISOString().slice(0, 10)}.csv`,
                        ["Supplier", "Revenue", "COGS", "Platform Fees", "Net Profit", "Net Margin %", "Units Sold", "Distinct Items"],
                        sorted.map((s) => [s.supplier, s.revenue.toFixed(2), s.cogs.toFixed(2), s.platform_fees.toFixed(2), s.net_profit.toFixed(2), s.net_margin_pct, s.units_sold, s.distinct_items]),
                        [
                            ["Revenue", totals.revenue.toFixed(2)], ["COGS", totals.cogs.toFixed(2)],
                            ["Platform Fees", totals.platform_fees.toFixed(2)],
                            ["Net Profit", totals.net_profit.toFixed(2)], ["Net Margin %", totals.net_margin_pct],
                            ["Units Sold", totals.units_sold],
                        ]
                    )} />
                    <DateRangePicker range={range} setRange={setRange} />
                </div>
            </div>

            {(totals.missing_cost_count || 0) > 0 && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-xs text-amber-800 flex items-center gap-2">
                        <AlertTriangle size={16} />
                        <span><b>{totals.missing_cost_count} order lines</b> in this range have no cost, so supplier profit and margins are overstated.</span>
                    </p>
                    <button type="button" onClick={() => drillRange(true)}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700">
                        See which ones
                    </button>
                </div>
            )}

            <RefreshableSection refreshing={refreshing} className="mb-6">
                {(() => {
                    const rows = data.rows || [];
                    if (rows.length === 0) return null;

                    const topRevenue = [...rows].sort((a, b) => b.revenue - a.revenue)[0];
                    const topUnits = [...rows].sort((a, b) => b.units_sold - a.units_sold)[0];
                    // Only rank by margin among suppliers with real revenue —
                    // a supplier with $0 revenue trivially shows 0% margin,
                    // which isn't a meaningful "best margin" result.
                    const withRevenue = rows.filter((r) => r.revenue > 0);
                    const topMargin = withRevenue.length > 0
                        ? [...withRevenue].sort((a, b) => b.net_margin_pct - a.net_margin_pct)[0]
                        : null;
                    const topItems = [...rows].sort((a, b) => b.distinct_items - a.distinct_items)[0];

                    return (
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <KpiCard label="Suppliers" value={rows.length} color="teal" />
                            <ClickableTotal onClick={() => openSupplier(topRevenue)}>
                                <KpiCard label="Top Revenue Supplier" value={topRevenue.supplier}
                                    sublabel={`$${topRevenue.revenue.toFixed(2)}`} color="green" />
                            </ClickableTotal>
                            <ClickableTotal onClick={() => openSupplier(topUnits)}>
                                <KpiCard label="Top Units Supplier" value={topUnits.supplier}
                                    sublabel={`${topUnits.units_sold} units sold`} color="blue" />
                            </ClickableTotal>
                            {topMargin && (
                                <ClickableTotal onClick={() => openSupplier(topMargin)}>
                                    <KpiCard label="Best Margin Supplier" value={topMargin.supplier}
                                        sublabel={`${topMargin.net_margin_pct}% net margin`}
                                        color={topMargin.net_margin_pct >= 0 ? "purple" : "red"} />
                                </ClickableTotal>
                            )}
                            <ClickableTotal onClick={() => openSupplier(topItems)}>
                                <KpiCard label="Most Distinct Items" value={topItems.supplier}
                                    sublabel={`${topItems.distinct_items} SKUs`} color="orange" />
                            </ClickableTotal>
                        </div>
                    );
                })()}
            </RefreshableSection>

            <RefreshableSection refreshing={refreshing}>
                <div className="bg-white rounded-2xl shadow-lg p-5 overflow-x-auto max-h-[70vh] overflow-y-auto">
                    <p className="text-[11px] text-gray-400 mb-2">Click a supplier (including Unassigned) to see its best-selling listings and missing costs</p>
                    <table className="w-full text-xs">
                        <thead className="sticky top-0 z-10 bg-white">
                            <tr className="text-left text-gray-400 border-b border-gray-100">
                                <SortableTH label="Supplier" sortKey="supplier" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Revenue" sortKey="revenue" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="COGS" sortKey="cogs" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Platform Fees" sortKey="platform_fees" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Net Profit" sortKey="net_profit" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Net Margin %" sortKey="net_margin_pct" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Units Sold" sortKey="units_sold" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Distinct Items" sortKey="distinct_items" sortConfig={sortConfig} onSort={requestSort} />
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.map((s) => (
                                <tr key={s.supplier} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" onClick={() => openSupplier(s)}>
                                    <td className="py-2 font-semibold whitespace-nowrap">
                                        {s.supplier}
                                        {(s.missing_cost_count || 0) > 0 && (
                                            <button type="button" onClick={(e) => { e.stopPropagation(); openSupplier(s, true); }}
                                                className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full hover:bg-amber-200">
                                                <AlertTriangle size={10} /> {s.missing_cost_count} missing cost
                                            </button>
                                        )}
                                    </td>
                                    <td className="py-2">${s.revenue.toFixed(2)}</td>
                                    <td className="py-2">${s.cogs.toFixed(2)}</td>
                                    <td className="py-2">
                                        <SupplierFeeHover supplier={s} />
                                    </td>
                                    <td className={`py-2 font-bold ${s.net_profit >= 0 ? "text-green-600" : "text-red-600"}`}>${s.net_profit.toFixed(2)}</td>
                                    <td className="py-2">{s.net_margin_pct}%</td>
                                    <td className="py-2">{s.units_sold}</td>
                                    <td className="py-2">{s.distinct_items}</td>
                                </tr>
                            ))}
                            {pageItems.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-gray-400">No orders in this range</td></tr>}
                        </tbody>
                    </table>
                    <Pagination page={page} totalPages={totalPages} setPage={setPage} totalItems={sorted.length} pageSize={pageSize} />
                </div>
            </RefreshableSection>

            <LineItemsDrawer
                open={!!breakdown}
                onClose={() => setBreakdown(null)}
                onCostSaved={handleForceRefresh}
                {...(breakdown || {})}
            />
        </div>
    );
};

export default SupplierReportPage;